import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { KeyIcon } from '../components/EscapeIcons';

const REMEMBER_EMAIL_KEY = 'oe_remember_email';
const REMEMBER_PWD_KEY = 'oe_remember_pwd';
const REMEMBER_FLAG_KEY = 'oe_remember_me';

// Light obfuscation only — localStorage is per-origin. NOT a security boundary.
const enc = (s) => { try { return btoa(unescape(encodeURIComponent(s))); } catch { return ''; } };
const dec = (s) => { try { return decodeURIComponent(escape(atob(s || ''))); } catch { return ''; } };

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Restore saved email + password on mount (one-click login)
  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
    const savedPwd = dec(localStorage.getItem(REMEMBER_PWD_KEY) || '');
    const savedFlag = localStorage.getItem(REMEMBER_FLAG_KEY);
    if (savedEmail) setEmail(savedEmail);
    if (savedPwd) setPassword(savedPwd);
    if (savedFlag !== null) setRememberMe(savedFlag === '1');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, email);
        localStorage.setItem(REMEMBER_PWD_KEY, enc(password));
        localStorage.setItem(REMEMBER_FLAG_KEY, '1');
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
        localStorage.removeItem(REMEMBER_PWD_KEY);
        localStorage.setItem(REMEMBER_FLAG_KEY, '0');
        // Also clear stored tokens so the user is logged out when the tab closes
        sessionStorage.setItem('oe_session_only', '1');
      }
      navigate('/admin');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-brand/10 flex items-center justify-center mb-4">
            <KeyIcon size={32} className="text-brand" />
          </div>
          <h1 className="font-heading text-3xl tracking-tight uppercase text-white">
            Admin <span className="text-brand">Vault</span>
          </h1>
          <p className="font-sans text-sm text-neutral-400 mt-2">Authorized personnel only</p>
        </div>

        <form
          onSubmit={handleSubmit}
          method="post"
          action="/admin/login"
          className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 space-y-4"
        >
          {error && (
            <div data-testid="login-error" className="bg-brand/10 border border-brand/30 rounded-xl p-3 text-sm text-brand font-sans">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="admin-email" className="font-heading text-xs tracking-widest uppercase text-neutral-400 mb-1 block">Email</label>
            <input
              id="admin-email"
              name="email"
              data-testid="login-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="username"
              className="w-full bg-black border border-neutral-700 rounded-xl px-4 py-3 text-white font-sans text-sm focus:border-brand focus:outline-none transition-colors"
              required
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="font-heading text-xs tracking-widest uppercase text-neutral-400 mb-1 block">Password</label>
            <input
              id="admin-password"
              name="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-black border border-neutral-700 rounded-xl px-4 py-3 text-white font-sans text-sm focus:border-brand focus:outline-none transition-colors"
              required
            />
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="remember-me-label">
            <span className="relative inline-flex items-center">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={e => setRememberMe(e.target.checked)}
                data-testid="remember-me-checkbox"
                className="peer appearance-none w-4 h-4 border border-neutral-600 rounded checked:bg-brand checked:border-brand transition-colors focus:outline-none focus:ring-2 focus:ring-brand/40"
              />
              <svg className="absolute left-0 top-0 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity" viewBox="0 0 16 16" fill="none">
                <path d="M3.5 8L6.5 11L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="font-sans text-xs text-neutral-300 group-hover:text-white transition-colors">
              Save email & password for one-click login
            </span>
          </label>

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full font-heading tracking-widest uppercase bg-brand text-white py-3 rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-50"
          >
            {loading ? 'Unlocking...' : 'Unlock'}
          </button>

          <p className="font-sans text-[10px] text-neutral-500 text-center pt-1">
            Credentials are stored locally on this device. Uncheck "Save" on shared devices.
          </p>
        </form>

        <div className="text-center mt-6">
          <a href="/" className="font-sans text-sm text-neutral-500 hover:text-brand transition-colors">
            Back to site
          </a>
        </div>
      </div>
    </div>
  );
}
